import {
	Add01Icon,
	AlertCircleIcon,
	ArrowLeft02Icon,
	Delete02Icon,
	Download01Icon,
	FileDownloadIcon,
	PencilEdit01Icon,
	UserRemove01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Head, Link, router, useForm } from "@inertiajs/react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { CheckCard } from "@/components/check-card";
import {
	type Conflict,
	ConflictDialog,
	findConflicts,
	mergeValues,
} from "@/components/conflict-dialog";
import { type Country, CountrySelect } from "@/components/country-select";
import Dashboard from "@/components/dashboard";
import { LegalFormAutocomplete } from "@/components/legal-form-autocomplete";
import { type Person, PersonForm } from "@/components/person-form";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogMedia,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { VerificationCard } from "@/components/verification-card";
import { VerificationStatusBadge } from "@/components/verification-status-badge";
import { WarningBadge } from "@/components/warning-badge";
import { formatBirthNumber } from "@/lib/formatters";
import { ROLE_LABELS } from "@/lib/labels";
import type ClientsBusinessClient from "@/types/serializers/ClientsBusinessClient";
import type ClientsBusinessClientPerson from "@/types/serializers/ClientsBusinessClientPerson";
import type HomeUser from "@/types/serializers/HomeUser";

interface Props {
	user: HomeUser;
	client_id: number;
	business_client: ClientsBusinessClient;
	countries: Country[];
	legal_forms: string[];
}

interface EntityFormData {
	business_client: {
		name: string;
		ico: string;
		legal_form: string | null;
		street: string | null;
		city: string | null;
		postal_code: string | null;
		country: string | null;
	};
}

const ENTITY_FIELD_LABELS: Record<string, string> = {
	name: "Název společnosti",
	legal_form: "Právní forma",
	street: "Ulice a číslo popisné",
	city: "Město",
	postal_code: "PSČ",
	country: "Země",
};

export default function BusinessClientShow({
	user,
	client_id,
	business_client,
	countries,
	legal_forms,
}: Props) {
	const [editingBcpId, setEditingBcpId] = useState<number | null>(null);
	const [isAdding, setIsAdding] = useState(false);

	const editingBcp = editingBcpId
		? (business_client.business_client_persons.find(
				(p) => p.id === editingBcpId,
			) ?? null)
		: null;
	const [personFormErrors, setPersonFormErrors] = useState<
		Partial<Record<string, string>>
	>({});
	const [pending, setPending] = useState<{
		conflicts: Conflict[];
		mergedValues: Partial<EntityFormData["business_client"]>;
		aresPersons: Array<{
			first_name: string;
			last_name: string;
			street: string | null;
			city: string | null;
			postal_code: string | null;
			country: string | null;
		}>;
		description: string;
	}>();

	const { data, setData, patch, processing, errors, isDirty } =
		useForm<EntityFormData>({
			business_client: {
				name: business_client.name,
				ico: business_client.ico,
				legal_form: business_client.legal_form,
				street: business_client.street,
				city: business_client.city,
				postal_code: business_client.postal_code,
				country: business_client.country || "CZ",
			},
		});

	const defaults: Partial<EntityFormData["business_client"]> = {
		country: "CZ",
	};

	const aresMutation = useMutation({
		mutationFn: async (ico: string) => {
			const response = await fetch(`/ares/lookup_vr?ico=${ico}`);
			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Nepodařilo se načíst data z ARES");
			}
			return response.json();
		},
		onSuccess: (result) => {
			const entity = result.entity;
			const statutoryMembers: Array<{
				first_name: string;
				last_name: string;
				street?: string;
				city?: string;
				postal_code?: string;
				country?: string;
			}> = result.statutory_members || [];

			const entityValues: Partial<EntityFormData["business_client"]> = {
				name: entity.name || data.business_client.name,
				legal_form: entity.legal_form || data.business_client.legal_form,
				street: entity.street || data.business_client.street,
				city: entity.city || data.business_client.city,
				postal_code: entity.postal_code || data.business_client.postal_code,
				country: entity.country || data.business_client.country,
			};

			const mergedValues = mergeValues(data.business_client, entityValues);
			const conflicts = findConflicts(
				data.business_client,
				entityValues,
				ENTITY_FIELD_LABELS,
				defaults,
			);

			const aresPersons = statutoryMembers.map((member) => ({
				first_name: member.first_name || "",
				last_name: member.last_name || "",
				street: member.street || null,
				city: member.city || null,
				postal_code: member.postal_code || null,
				country: member.country || "CZ",
			}));

			// Check for persons conflict
			const existingPersons = business_client.business_client_persons.map(
				(bcp) => ({
					first_name: bcp.physical_person.first_name,
					last_name: bcp.physical_person.last_name,
				}),
			);

			const formatFullName = (p: { first_name: string; last_name: string }) => {
				return `${p.first_name} ${p.last_name}`;
			};

			if (existingPersons.length > 0 && aresPersons.length > 0) {
				conflicts.push({
					field: "persons",
					label: "Osoby",
					oldValue: existingPersons.map(formatFullName).join(", "),
					newValue: aresPersons.map(formatFullName).join(", "),
				});
			}

			if (conflicts.length === 0) {
				setData("business_client", {
					...data.business_client,
					...mergedValues,
				});
				// Add ARES persons if there are none existing
				if (existingPersons.length === 0 && aresPersons.length > 0) {
					router.post(
						`/business_clients/${business_client.id}/replace_persons`,
						{ persons: aresPersons },
						{ preserveScroll: true },
					);
				}
			} else {
				setPending({
					conflicts,
					mergedValues,
					aresPersons,
					description: "Údaje byly načteny z ARES.",
				});
			}
		},
		onError: (error: Error) => {
			alert(error.message);
		},
	});

	const handleApply = () => {
		if (!pending) return;
		setData("business_client", {
			...data.business_client,
			...pending.mergedValues,
		});

		// Check if persons conflict was included
		const hasPersonsConflict = pending.conflicts.some(
			(c) => c.field === "persons",
		);
		if (hasPersonsConflict && pending.aresPersons.length > 0) {
			router.post(
				`/business_clients/${business_client.id}/replace_persons`,
				{ persons: pending.aresPersons },
				{ preserveScroll: true },
			);
		}

		setPending(undefined);
	};

	const handleCancelConflict = () => {
		setPending(undefined);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		patch(`/business_clients/${business_client.id}`);
	};

	const handleDelete = () => {
		router.delete(`/clients/${client_id}`);
	};

	const bcpToFormPerson = (bcp: ClientsBusinessClientPerson): Person => {
		const person = bcp.physical_person;
		return {
			_key: String(bcp.id),
			physical_person: {
				first_name: person.first_name,
				last_name: person.last_name,
				birth_number: person.birth_number ?? "",
				street: person.street,
				city: person.city,
				postal_code: person.postal_code,
				country: person.country,
				document_type: person.document_type || null,
				document_number: person.document_number || null,
				document_expiry_date: person.document_expiry_date || null,
				document_issuer: person.document_issuer || null,
				birth_date: person.birth_date,
				birth_place: person.birth_place,
				birth_country: person.birth_country,
				citizenship: person.citizenship,
				citizenship_other: person.citizenship_other,
				gender: person.gender,
				is_sanctioned: person.is_sanctioned,
				is_pep: person.is_pep,
				pep_info: person.pep_info,
			},
			is_ubo: bcp.is_ubo,
			is_statutory_member: bcp.is_statutory_member,
			other_role: bcp.other_role ?? null,
		};
	};

	const handleEditPerson = (bcp: ClientsBusinessClientPerson) => {
		setEditingBcpId(bcp.id);
		setIsAdding(false);
	};

	const handleAddPerson = () => {
		setEditingBcpId(null);
		setIsAdding(true);
	};

	const handleSavePerson = (person: Person, editingPerson?: { id: number }) => {
		setPersonFormErrors({});
		if (isAdding) {
			router.post(
				"/business_client_persons",
				{
					business_client_id: business_client.id,
					is_ubo: person.is_ubo,
					is_statutory_member: person.is_statutory_member,
					other_role: person.other_role,
					physical_person: {
						...person.physical_person,
						id: editingPerson?.id ?? null,
						birth_number: person.physical_person.birth_number || null,
					},
				},
				{
					preserveScroll: true,
					onSuccess: () => {
						setIsAdding(false);
						setPersonFormErrors({});
					},
					onError: (errors) => {
						setPersonFormErrors(errors);
					},
				},
			);
		} else if (editingBcp) {
			router.patch(
				`/business_client_persons/${editingBcp.id}`,
				{
					is_ubo: person.is_ubo,
					is_statutory_member: person.is_statutory_member,
					other_role: person.other_role,
					physical_person: {
						...person.physical_person,
						id: editingPerson?.id ?? null,
						birth_number: person.physical_person.birth_number || null,
					},
				},
				{
					preserveScroll: true,
					onSuccess: () => {
						setEditingBcpId(null);
						setPersonFormErrors({});
					},
					onError: (errors) => {
						setPersonFormErrors(errors);
					},
				},
			);
		}
	};

	const [deletingPerson, setDeletingPerson] =
		useState<ClientsBusinessClientPerson | null>(null);

	const handleDeletePerson = (bcp: ClientsBusinessClientPerson) => {
		setDeletingPerson(bcp);
	};

	const confirmDeletePerson = () => {
		if (deletingPerson) {
			router.delete(`/business_client_persons/${deletingPerson.id}`, {
				preserveScroll: true,
			});
			setDeletingPerson(null);
		}
	};

	const getEditingPerson = (): Person | null => {
		if (editingBcp) return bcpToFormPerson(editingBcp);
		if (isAdding) {
			return {
				_key: crypto.randomUUID(),
				physical_person: {
					first_name: "",
					last_name: "",
					birth_number: "",
					street: null,
					city: null,
					postal_code: null,
					country: "CZ",
					document_type: null,
					document_number: null,
					document_expiry_date: null,
					document_issuer: null,
					birth_date: null,
					birth_place: null,
					birth_country: "CZ",
					citizenship: null,
					citizenship_other: null,
					gender: null,
					is_sanctioned: null,
					is_pep: null,
					pep_info: null,
				},
				is_ubo: false,
				is_statutory_member: false,
				other_role: null,
			};
		}
		return null;
	};

	const currentPerson = getEditingPerson();
	const sheetOpen = editingBcp !== null || isAdding;

	return (
		<>
			<Head title={business_client.name} />
			<Dashboard user={user}>
				<form
					onSubmit={handleSubmit}
					className="flex flex-1 flex-col overflow-hidden"
				>
					<div className="overflow-y-auto p-4 lg:p-6">
						<div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
							<div className="flex items-center justify-between">
								<div className="flex items-start gap-4">
									<Button
										type="button"
										variant="ghost"
										size="icon"
										render={<Link href="/clients" />}
									>
										<HugeiconsIcon icon={ArrowLeft02Icon} strokeWidth={2} />
									</Button>
									<div>
										<div className="flex items-center gap-3">
											<h2 className="font-semibold text-2xl">
												{business_client.name}
											</h2>
											<Badge variant="outline">Právnická osoba</Badge>
										</div>
									</div>
								</div>
								<div className="flex gap-2">
									<AlertDialog>
										<AlertDialogTrigger
											render={
												<Button type="button" variant="destructive">
													<HugeiconsIcon
														icon={Delete02Icon}
														strokeWidth={2}
														data-icon="inline-start"
													/>
													Smazat
												</Button>
											}
										/>
										<AlertDialogContent size="sm">
											<AlertDialogHeader>
												<AlertDialogMedia className="bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive">
													<HugeiconsIcon icon={Delete02Icon} strokeWidth={2} />
												</AlertDialogMedia>
												<AlertDialogTitle>Smazat klienta?</AlertDialogTitle>
												<AlertDialogDescription>
													Tato akce je nevratná.
												</AlertDialogDescription>
											</AlertDialogHeader>
											<AlertDialogFooter>
												<AlertDialogCancel variant="outline">
													Zrušit
												</AlertDialogCancel>
												<AlertDialogAction
													variant="destructive"
													onClick={handleDelete}
												>
													Smazat
												</AlertDialogAction>
											</AlertDialogFooter>
										</AlertDialogContent>
									</AlertDialog>
								</div>
							</div>

							<div className="inline-flex">
								<CheckCard
									checked={business_client.checked}
									checkedAt={business_client.checked_at}
									checkExpiresAt={business_client.check_expires_at}
									checkUrl={`/business_clients/${business_client.id}/check`}
									uncheckUrl={`/business_clients/${business_client.id}/uncheck`}
								/>
							</div>

							<Card>
								<CardHeader>
									<CardTitle>
										IČO <span className="text-destructive">*</span>
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="flex gap-2">
										<Field
											className="flex-1"
											data-invalid={!!errors["business_client.ico"]}
										>
											<Input
												id="ico"
												value={data.business_client.ico}
												onChange={(e) =>
													setData("business_client", {
														...data.business_client,
														ico: e.target.value,
													})
												}
												placeholder="12345678"
												aria-invalid={!!errors["business_client.ico"]}
											/>
											<FieldError>{errors["business_client.ico"]}</FieldError>
										</Field>
										<Button
											type="button"
											variant="outline"
											onClick={() =>
												aresMutation.mutate(data.business_client.ico)
											}
											disabled={
												!data.business_client.ico || aresMutation.isPending
											}
										>
											<HugeiconsIcon
												icon={Download01Icon}
												strokeWidth={2}
												data-icon="inline-start"
											/>
											{aresMutation.isPending ? "Načítám..." : "Načíst z ARES"}
										</Button>
									</div>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>Údaje o společnosti</CardTitle>
								</CardHeader>
								<CardContent>
									<FieldGroup>
										<Field data-invalid={!!errors["business_client.name"]}>
											<FieldLabel htmlFor="name">
												Název společnosti{" "}
												<span className="text-destructive">*</span>
											</FieldLabel>
											<Input
												id="name"
												value={data.business_client.name}
												onChange={(e) =>
													setData("business_client", {
														...data.business_client,
														name: e.target.value,
													})
												}
												placeholder="Společnost s.r.o."
												aria-invalid={!!errors["business_client.name"]}
											/>
											<FieldError>{errors["business_client.name"]}</FieldError>
										</Field>
										<Field>
											<FieldLabel htmlFor="legal_form">Právní forma</FieldLabel>
											<LegalFormAutocomplete
												value={data.business_client.legal_form || ""}
												onChange={(value) =>
													setData("business_client", {
														...data.business_client,
														legal_form: value || null,
													})
												}
												legalForms={legal_forms}
											/>
										</Field>
									</FieldGroup>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<div className="flex items-center justify-between gap-2">
										<CardTitle>Sídlo</CardTitle>
										{business_client.missing_address && (
											<WarningBadge label="Chybí adresa" />
										)}
									</div>
								</CardHeader>
								<CardContent>
									<FieldGroup>
										<Field
											data-warning={
												business_client.missing_address &&
												!data.business_client.street
											}
										>
											<FieldLabel htmlFor="street">
												Ulice a číslo popisné
											</FieldLabel>
											<Input
												id="street"
												value={data.business_client.street || ""}
												onChange={(e) =>
													setData("business_client", {
														...data.business_client,
														street: e.target.value || null,
													})
												}
												placeholder="Ulice 123/4"
												data-warning={
													business_client.missing_address &&
													!data.business_client.street
												}
											/>
										</Field>
										<div className="flex gap-4">
											<Field
												className="w-full"
												data-warning={
													business_client.missing_address &&
													!data.business_client.city
												}
											>
												<FieldLabel htmlFor="city">Město</FieldLabel>
												<Input
													id="city"
													value={data.business_client.city || ""}
													onChange={(e) =>
														setData("business_client", {
															...data.business_client,
															city: e.target.value || null,
														})
													}
													placeholder="Praha"
													data-warning={
														business_client.missing_address &&
														!data.business_client.city
													}
												/>
											</Field>
											<Field
												className="w-68"
												data-warning={
													business_client.missing_address &&
													!data.business_client.postal_code
												}
												data-invalid={!!errors["business_client.postal_code"]}
											>
												<FieldLabel htmlFor="postal_code">PSČ</FieldLabel>
												<Input
													id="postal_code"
													value={data.business_client.postal_code || ""}
													onChange={(e) =>
														setData("business_client", {
															...data.business_client,
															postal_code: e.target.value || null,
														})
													}
													placeholder="110 00"
													data-warning={
														business_client.missing_address &&
														!data.business_client.postal_code
													}
													aria-invalid={!!errors["business_client.postal_code"]}
												/>
												<FieldError>
													{errors["business_client.postal_code"]}
												</FieldError>
											</Field>
											<Field className="w-68">
												<FieldLabel htmlFor="country">Země</FieldLabel>
												<CountrySelect
													value={data.business_client.country || ""}
													onChange={(value) =>
														setData("business_client", {
															...data.business_client,
															country: value,
														})
													}
													countries={countries}
												/>
											</Field>
										</div>
									</FieldGroup>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<div className="flex items-center justify-between gap-2">
										<CardTitle>Osoby</CardTitle>
										<div className="flex gap-2">
											{business_client.missing_ubo && (
												<WarningBadge label="Chybí skutečný majitel" />
											)}
											{business_client.missing_statutory_member && (
												<WarningBadge label="Chybí statutární zástupce" />
											)}
											<Button
												type="button"
												variant="outline"
												onClick={handleAddPerson}
											>
												<HugeiconsIcon
													icon={Add01Icon}
													strokeWidth={2}
													data-icon="inline-start"
												/>
												Přidat osobu
											</Button>
										</div>
									</div>
								</CardHeader>
								<CardContent>
									{business_client.business_client_persons.length > 0 ? (
										<div className="rounded-xl border">
											<Table>
												<TableHeader>
													<TableRow>
														<TableHead>Jméno</TableHead>
														<TableHead>RČ</TableHead>
														<TableHead>Role</TableHead>
														<TableHead>PEP</TableHead>
														<TableHead>Stav</TableHead>
														<TableHead className="w-20" />
													</TableRow>
												</TableHeader>
												<TableBody>
													{business_client.business_client_persons.map(
														(bcp) => {
															const person = bcp.physical_person;
															const hasWarnings =
																person.missing_birth_number ||
																person.missing_address ||
																person.missing_document ||
																person.missing_birth_info ||
																person.missing_citizenship ||
																person.missing_gender ||
																person.missing_aml_check ||
																person.document_expiring ||
																person.document_expired ||
																bcp.verification_expiring;
															const hasErrors = person.document_expired;
															const warningLabels: string[] = [];
															if (person.missing_birth_number)
																warningLabels.push("rodné číslo");
															if (person.missing_address)
																warningLabels.push("adresa bydliště");
															if (person.missing_document)
																warningLabels.push("doklad totožnosti");
															if (person.missing_birth_info)
																warningLabels.push("údaje o narození");
															if (person.missing_citizenship)
																warningLabels.push("státní občanství");
															if (person.missing_gender)
																warningLabels.push("pohlaví");
															if (person.missing_aml_check)
																warningLabels.push("AML kontrola (sankce/PEP)");
															if (person.document_expired)
																warningLabels.push("dokument expiroval");
															if (person.document_expiring)
																warningLabels.push("dokument brzy expiruje");
															if (bcp.verification_expiring)
																warningLabels.push(
																	"identifikace brzy expiruje",
																);

															return (
																<TableRow
																	key={bcp.id}
																	className="cursor-pointer"
																	onClick={() => handleEditPerson(bcp)}
																>
																	<TableCell className="font-medium">
																		<div className="flex items-center gap-2">
																			{person.full_name}
																			{hasWarnings && (
																				<Tooltip>
																					<TooltipTrigger>
																						<HugeiconsIcon
																							icon={AlertCircleIcon}
																							strokeWidth={2}
																							className={`size-4 ${hasErrors ? "text-destructive" : "text-warning"}`}
																						/>
																					</TooltipTrigger>
																					<TooltipContent>
																						<div className="flex flex-col gap-1">
																							<span className="font-medium">
																								Upozornění:
																							</span>
																							<ul className="list-inside list-disc">
																								{warningLabels.map((label) => (
																									<li key={label}>{label}</li>
																								))}
																							</ul>
																						</div>
																					</TooltipContent>
																				</Tooltip>
																			)}
																		</div>
																	</TableCell>
																	<TableCell>
																		{formatBirthNumber(person.birth_number) || (
																			<span className="text-muted-foreground">
																				—
																			</span>
																		)}
																	</TableCell>
																	<TableCell>
																		<Tooltip>
																			<TooltipTrigger>
																				<span className="block max-w-32 truncate">
																					{bcp.roles
																						.map((r) => ROLE_LABELS[r] ?? r)
																						.join(", ")}
																				</span>
																			</TooltipTrigger>
																			<TooltipContent>
																				{bcp.roles
																					.map((r) => ROLE_LABELS[r] ?? r)
																					.join(", ")}
																			</TooltipContent>
																		</Tooltip>
																	</TableCell>
																	<TableCell>
																		{person.is_pep === true ? (
																			<Badge variant="warning">Ano</Badge>
																		) : person.is_pep === false ? (
																			<Badge variant="secondary">Ne</Badge>
																		) : (
																			<span className="text-muted-foreground">
																				—
																			</span>
																		)}
																	</TableCell>
																	<TableCell>
																		<VerificationStatusBadge
																			entries={[
																				{
																					verified: bcp.verified,
																					verification_expires_at:
																						bcp.verification_expires_at,
																					verification_expiring_soon:
																						bcp.verification_expiring,
																				},
																			]}
																		/>
																	</TableCell>
																	<TableCell className="w-20">
																		<div className="flex gap-1">
																			<Button
																				type="button"
																				variant="ghost"
																				size="icon-sm"
																				onClick={(e) => {
																					e.stopPropagation();
																					handleEditPerson(bcp);
																				}}
																			>
																				<HugeiconsIcon
																					icon={PencilEdit01Icon}
																					strokeWidth={2}
																				/>
																			</Button>
																			<Button
																				type="button"
																				variant="ghost"
																				size="icon-sm"
																				onClick={(e) => {
																					e.stopPropagation();
																					handleDeletePerson(bcp);
																				}}
																			>
																				<HugeiconsIcon
																					icon={Delete02Icon}
																					strokeWidth={2}
																				/>
																			</Button>
																		</div>
																	</TableCell>
																</TableRow>
															);
														},
													)}
												</TableBody>
											</Table>
										</div>
									) : (
										<div className="rounded-xl border py-8 text-center text-muted-foreground text-sm">
											Žádné osoby nebyly přidány.
										</div>
									)}
								</CardContent>
							</Card>

							{pending && (
								<ConflictDialog
									conflicts={pending.conflicts}
									description={pending.description}
									onApply={handleApply}
									onCancel={handleCancelConflict}
								/>
							)}

							<AlertDialog
								open={deletingPerson !== null}
								onOpenChange={(open) => {
									if (!open) setDeletingPerson(null);
								}}
							>
								<AlertDialogContent size="sm">
									<AlertDialogHeader>
										<AlertDialogMedia className="bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive">
											<HugeiconsIcon icon={UserRemove01Icon} strokeWidth={2} />
										</AlertDialogMedia>
										<AlertDialogTitle>Odebrat osobu?</AlertDialogTitle>
										<AlertDialogDescription>
											{deletingPerson?.physical_person.full_name} bude odebrán/a
											z této právnické osoby.
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel variant="outline">
											Zrušit
										</AlertDialogCancel>
										<AlertDialogAction
											variant="destructive"
											onClick={confirmDeletePerson}
										>
											Odebrat
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
						</div>
					</div>

					<div className="border-t bg-background px-4 py-4 lg:px-6">
						<div className="mx-auto flex w-full max-w-2xl items-center justify-between">
							<DropdownMenu>
								<DropdownMenuTrigger
									render={
										<Button type="button" variant="outline">
											<HugeiconsIcon
												icon={FileDownloadIcon}
												strokeWidth={2}
												data-icon="inline-start"
											/>
											Formuláře
										</Button>
									}
								/>
								<DropdownMenuContent align="start" side="top">
									<DropdownMenuItem
										render={
											<a
												href={`/client_control_pdfs/business_client/${business_client.id}`}
											/>
										}
									>
										<HugeiconsIcon icon={Download01Icon} strokeWidth={2} />
										Záznam o kontrole klienta
									</DropdownMenuItem>
									<DropdownMenuItem
										render={
											<a
												href={`/cooperation_refusal_pdfs/business_client/${business_client.id}`}
											/>
										}
									>
										<HugeiconsIcon icon={Download01Icon} strokeWidth={2} />
										Odmítnutí spolupráce
									</DropdownMenuItem>
									<DropdownMenuItem
										render={
											<a
												href={`/discrepancy_notice_pdfs/business_client/${business_client.id}`}
											/>
										}
									>
										<HugeiconsIcon icon={Download01Icon} strokeWidth={2} />
										Upozornění na nesrovnalost
									</DropdownMenuItem>
									<DropdownMenuItem
										render={
											<a
												href={`/suspicious_transaction_pdfs/business_client/${business_client.id}`}
											/>
										}
									>
										<HugeiconsIcon icon={Download01Icon} strokeWidth={2} />
										Podezřelá transakce
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
							<div className="flex gap-4">
								<Button
									type="button"
									variant="outline"
									size="lg"
									render={<Link href="/clients" />}
								>
									Zpět
								</Button>
								<Button
									type="submit"
									size="lg"
									disabled={processing || !isDirty}
								>
									Uložit změny
								</Button>
							</div>
						</div>
					</div>
				</form>

				<Sheet
					open={sheetOpen}
					onOpenChange={(open) => {
						if (!open) {
							setEditingBcpId(null);
							setIsAdding(false);
						}
					}}
				>
					<SheetContent
						side="right"
						className="gap-0 overflow-y-auto data-[side=right]:sm:max-w-2xl"
					>
						<SheetHeader className="border-b">
							<SheetTitle>
								{isAdding ? "Přidat osobu" : "Upravit osobu"}
							</SheetTitle>
							<SheetDescription>
								{isAdding
									? "Vyplňte údaje o osobě spojené s právnickou osobou."
									: "Upravte údaje o osobě spojené s právnickou osobou."}
							</SheetDescription>
						</SheetHeader>
						{currentPerson && (
							<PersonForm
								person={currentPerson}
								countries={countries}
								errors={personFormErrors}
								warnings={
									editingBcp
										? {
												birth_number:
													editingBcp.physical_person.missing_birth_number,
												address: editingBcp.physical_person.missing_address,
												document: editingBcp.physical_person.missing_document,
												document_expiring:
													editingBcp.physical_person.document_expiring,
												document_expired:
													editingBcp.physical_person.document_expired,
												birth_info:
													editingBcp.physical_person.missing_birth_info,
												citizenship:
													editingBcp.physical_person.missing_citizenship,
												gender: editingBcp.physical_person.missing_gender,
												aml_check: editingBcp.physical_person.missing_aml_check,
											}
										: undefined
								}
								footerStart={
									editingBcp && (
										<Button
											variant="outline"
											render={
												<a
													href={`/identification_pdfs/business_client_person/${editingBcp.id}`}
												/>
											}
										>
											<HugeiconsIcon
												icon={Download01Icon}
												strokeWidth={2}
												data-icon="inline-start"
											/>
											Záznam o identifikaci
										</Button>
									)
								}
								mode={isAdding ? "create" : "update"}
								onSave={handleSavePerson}
								onCancel={() => {
									setEditingBcpId(null);
									setIsAdding(false);
									setPersonFormErrors({});
								}}
							>
								{editingBcp && (
									<div className="inline-flex">
										<VerificationCard
											verified={editingBcp.verified}
											verifiedAt={editingBcp.verified_at}
											verificationExpiresAt={editingBcp.verification_expires_at}
											verifyUrl={`/business_client_persons/${editingBcp.id}/verify`}
											unverifyUrl={`/business_client_persons/${editingBcp.id}/unverify`}
										/>
									</div>
								)}
							</PersonForm>
						)}
					</SheetContent>
				</Sheet>
			</Dashboard>
		</>
	);
}
